import { z } from "zod";

const BASE = import.meta.env.VITE_API_BASE_URL ?? "";

export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: string,
    public response: Response,
  ) {
    super(`API ${status}: ${detail}`);
  }
}

/**
 * Coerce a response `detail` into a readable string. FastAPI returns 422
 * validation errors as an ARRAY of `{type, loc, msg, ...}` objects — storing
 * that array as ApiError.detail (typed string) and rendering it crashes React
 * ("Objects are not valid as a React child"). Flatten to "loc: msg; …".
 */
function coerceDetail(raw: unknown, fallback: string): string {
  if (typeof raw === "string") return raw;
  if (Array.isArray(raw)) {
    return (
      raw
        .map((e) => {
          if (e && typeof e === "object" && "msg" in e) {
            const locArr = (e as { loc?: unknown[] }).loc;
            const loc = Array.isArray(locArr) ? locArr.slice(1).join(".") : "";
            const msg = String((e as { msg: unknown }).msg);
            return loc ? `${loc}: ${msg}` : msg;
          }
          return JSON.stringify(e);
        })
        .join("; ") || fallback
    );
  }
  if (raw == null) return fallback;
  return typeof raw === "object" ? JSON.stringify(raw) : String(raw);
}

async function request<T extends z.ZodTypeAny>(
  path: string,
  schema: T,
  init?: RequestInit,
): Promise<z.infer<T>> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
    ...init,
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      detail = coerceDetail(body.detail, detail);
    } catch {
      // body was not JSON; keep statusText
    }
    throw new ApiError(res.status, detail, res);
  }
  if (res.status === 204) return undefined as z.infer<T>;
  const json = await res.json();
  return schema.parse(json);
}

/** GET — schema-validated response. */
export function apiGet<T extends z.ZodTypeAny>(path: string, schema: T) {
  return request(path, schema);
}

/** POST — schema-validated response. */
export function apiPost<T extends z.ZodTypeAny>(
  path: string,
  schema: T,
  body?: unknown,
) {
  return request(path, schema, {
    method: "POST",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

/** PUT — schema-validated response. */
export function apiPut<T extends z.ZodTypeAny>(
  path: string,
  schema: T,
  body?: unknown,
) {
  return request(path, schema, {
    method: "PUT",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

/** DELETE — no response body expected. */
export async function apiDelete(path: string): Promise<void> {
  const res = await fetch(`${BASE}${path}`, {
    method: "DELETE",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      detail = coerceDetail(body.detail, detail);
    } catch {
      // body was not JSON; keep statusText
    }
    throw new ApiError(res.status, detail, res);
  }
}

/** POST with FormData (CSV uploads). */
export async function apiPostFormData<T extends z.ZodTypeAny>(
  path: string,
  schema: T,
  form: FormData,
) {
  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    credentials: "include",
    body: form,
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      detail = coerceDetail(body.detail, detail);
    } catch {
      // not JSON
    }
    throw new ApiError(res.status, detail, res);
  }
  return schema.parse(await res.json());
}

// ───── Schemas shared across the app ────────────────────────────────────

export const HealthSchema = z.object({ status: z.string() });
export const EmailSchema = z.object({ email: z.string().email() });
